import { Routes } from '@angular/router';
import { authGuard } from '@core/guards/auth.guard';
import { guestGuard } from '@core/guards/guest.guard';
import { AthleteLibraryDialogComponent } from '@features/performance-center/pages/athlete-library/athlete-library';
import { ModalComponent } from '@features/settings/pages/modal/modal';
import { tuiGenerateDialogableRoute as tuiRouteDialog } from '@taiga-ui/kit';

const athleteLibraryDialogRoutes = [
  'performance-center/athlete-library',
  'performance-center/athlete-library/athletes',
  'performance-center/athlete-library/athletes/create',
  'performance-center/athlete-library/athletes/:athleteId/edit',
  'performance-center/athlete-library/groups',
  'performance-center/athlete-library/groups/create',
  'performance-center/athlete-library/categories',
  'performance-center/athlete-library/categories/create',
  'performance-center/athlete-library/performance-limits',
].map((path) =>
  tuiRouteDialog(AthleteLibraryDialogComponent, {
    path,
    outlet: 'modal',
    appearance: 'taiga athlete-library',
  }),
);

const settingsDialogRoutes = [
  'settings/users-roles/users',
  'settings/users-roles/users/create',
  'settings/users-roles/users/:userId/edit',
  'settings/users-roles/roles',
  'settings/users-roles/roles/create',
  'settings/users-roles/roles/:roleId/edit',
  'settings/users-roles/roles/:roleId/copy',
  'settings/:section',
].map((path) =>
  tuiRouteDialog(ModalComponent, {
    path,
    outlet: 'modal',
    appearance: 'taiga settings',
  }),
);

export const routes: Routes = [
  // Authenticated
  {
    path: '',
    loadComponent: () => import('./layouts/main/main').then((m) => m.MainLayout),
    canActivate: [authGuard],
    children: [
      {
        path: 'dashboard',
        loadChildren: () =>
          import('./features/dashboard/dashboard.routes').then((m) => m.dashboardRoutes),
        data: {
          breadcrumb: 'Dashboard',
        },
      },
      {
        path: 'performance-center',
        loadChildren: () =>
          import('./features/performance-center/performance-center.routes').then(
            (m) => m.performanceCenterRoutes,
          ),
        data: {
          breadcrumb: 'Performance Center',
        },
      },
      {
        path: 'test-creation',
        loadChildren: () =>
          import('./features/test-creation/test-creation.routes').then((m) => m.testCreationRoutes),
        data: {
          breadcrumb: 'Test Creation',
        },
      },
      {
        path: 'college',
        loadChildren: () =>
          import('./features/college/college.routes').then((m) => m.collegeRoutes),
      },
      {
        path: 'features',
        loadChildren: () =>
          import('./features/features/features.routes').then((m) => m.featuresRoutes),
      },
      {
        path: 'training-zone-builder',
        loadChildren: () =>
          import('./features/training-zone-builder/training-zone-builder.routes').then(
            (m) => m.trainingZoneBuilderRoutes,
          ),
        data: {
          breadcrumb: 'Training Zone Builder',
        },
      },
      ...settingsDialogRoutes,
      ...athleteLibraryDialogRoutes,
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
    ],
  },

  // Report generator — full-bleed, sibling to the main shell (no app chrome).
  // Siblings don't inherit the parent authGuard, so it's declared explicitly.
  {
    path: 'report-generator',
    loadComponent: () => import('./layouts/report/report-layout').then((m) => m.ReportLayout),
    canActivate: [authGuard],
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/reporting/reporting.routes').then((m) => m.reportingRoutes),
      },
    ],
  },

  // Guests
  {
    path: 'auth',
    loadComponent: () => import('./layouts/full/full').then((m) => m.FullLayout),
    canActivate: [guestGuard],
    children: [
      {
        path: '',
        loadChildren: () => import('./features/auth/auth.routes').then((m) => m.authRoutes),
      },
    ],
  },

  // Legal notices — public, no auth required either way (accessible signed in or out).
  {
    path: 'legal',
    loadComponent: () => import('./layouts/full/full').then((m) => m.FullLayout),
    children: [
      {
        path: '',
        loadChildren: () => import('./features/legal/legal.routes').then((m) => m.legalRoutes),
      },
    ],
  },

  // Password recovery — public, no auth required either way (reached via emailed link).
  {
    path: 'forgot-password',
    loadComponent: () => import('./layouts/full/full').then((m) => m.FullLayout),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/auth/pages/forgot-password/forgot-password').then(
            (m) => m.ForgotPasswordPage,
          ),
        title: 'Forgot Password',
      },
    ],
  },
  {
    path: 'reset-password/:key',
    loadComponent: () => import('./layouts/full/full').then((m) => m.FullLayout),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/auth/pages/reset-password/reset-password').then(
            (m) => m.ResetPasswordPage,
          ),
        title: 'Reset Password',
      },
    ],
  },

  // Fallback
  {
    path: '**',
    loadComponent: () => import('./features/not-found/not-found').then((m) => m.NotFoundPage),
    title: 'Page Not Found',
  },
];
